import { detectHeaderAndMapping, parseMappedBoqRow } from '../domain/parsing'
import type { HeaderDetection } from '../domain/parsing'
import type { BoqRow, ColumnMapping, NumericParseIssue } from '../types'
import {
  BoqImportError,
  DATA_PREVIEW_ROWS,
  type ImportedCell,
  type ImportedSheetMetadata,
  type ParsedSheet,
} from './import/types'

export interface MappedBoqSheet {
  sourceType: BoqRow['sourceType']
  fileName: string
  sheetName: string
  mapping: ColumnMapping
  headerRow: number
  headers: string[]
  /** Comparable item rows, ready for matchBoqRows(). */
  rows: BoqRow[]
  /** Every parsed source row, including section/summary/excluded rows. */
  allRows: BoqRow[]
  excludedRows: BoqRow[]
  previewRows: BoqRow[]
  warnings: string[]
}

function domainCellValue(cell: ImportedCell): unknown {
  if (cell.kind !== 'formula') return cell.rawValue
  if (cell.formulaResultState === 'cached') {
    return { formula: cell.formula ?? '', result: cell.rawValue }
  }
  return { formula: cell.formula ?? '' }
}

function numericIssueMessage(row: BoqRow, issue: NumericParseIssue): string {
  const fieldLabel =
    issue.field === 'quantity' ? '工程量' : issue.field === 'unitPrice' ? '综合单价' : '合价'
  return issue.status === 'formula-unresolved'
    ? `第 ${String(row.sourceRowNumber)} 行“${fieldLabel}”为公式，但文件中没有可安全读取的缓存结果。`
    : `第 ${String(row.sourceRowNumber)} 行“${fieldLabel}”无法解析为数字。`
}

/**
 * Maps structured worker output through the shared pure domain parser. The
 * item-code display text is supplied separately so formatted leading zeroes
 * survive while numeric columns still use their typed/cached raw values.
 */
export function mapSheetToBoqRows(
  parsedSheet: ParsedSheet,
  mapping: ColumnMapping,
): MappedBoqSheet {
  if (mapping.headerRow === null || mapping.headerRow !== parsedSheet.headerRow) {
    throw new BoqImportError({
      code: 'INVALID_FILE',
      message: '字段映射的标题行与当前解析结果不一致，请重新读取工作表。',
      details: {
        mappingHeaderRow: mapping.headerRow,
        parsedHeaderRow: parsedSheet.headerRow,
      },
    })
  }

  const allRows = parsedSheet.rows.map((row) => {
    const cells = row.cells.map(domainCellValue)
    const mapped = parseMappedBoqRow({
      cells,
      displayCells: row.cells.map((cell) => cell.displayText),
      mapping,
      sourceType: parsedSheet.sourceType,
      fileName: parsedSheet.fileName,
      sheetName: parsedSheet.sheet.name,
      sourceRowNumber: row.sourceRowNumber,
    })
    return {
      ...mapped,
      rawCells: row.cells.map((cell) => ({ ...cell })),
    }
  })

  const rows = allRows.filter((row) => row.rowType === 'item' && row.exclusionReason === null)
  const excludedRows = allRows.filter(
    (row) => row.rowType !== 'item' || row.exclusionReason !== null,
  )
  const issueWarnings = allRows.flatMap((row) =>
    (row.numericParseIssues ?? []).map((issue) => numericIssueMessage(row, issue)),
  )

  return {
    sourceType: parsedSheet.sourceType,
    fileName: parsedSheet.fileName,
    sheetName: parsedSheet.sheet.name,
    mapping,
    headerRow: parsedSheet.headerRow,
    headers: parsedSheet.headerCells.map((cell) => cell.displayText),
    rows,
    allRows,
    excludedRows,
    previewRows: rows.slice(0, DATA_PREVIEW_ROWS),
    warnings: [...parsedSheet.warnings, ...issueWarnings],
  }
}

function scanMatrix(sheet: ImportedSheetMetadata): string[][] {
  const lastScannedRow = sheet.scanRows.at(-1)?.sourceRowNumber ?? 0
  const rows = Array.from({ length: lastScannedRow }, () => [] as string[])
  for (const row of sheet.scanRows) {
    rows[row.sourceRowNumber - 1] = row.cells.map((cell) => cell.displayText)
  }
  return rows
}

export function detectSheetMapping(sheet: ImportedSheetMetadata): HeaderDetection | null {
  return detectHeaderAndMapping(scanMatrix(sheet))
}
